/* window.BronteWishlist: toggle/has/list/count/add/remove/available, over localStorage key `bronte_wishlist`.
   Every mutation dispatches `bronte:wishlist:change` on document with { handles, count }. */
(() => {
  if (window.BronteWishlist) return;

  const KEY = 'bronte_wishlist';
  const EVENT = 'bronte:wishlist:change';

  const storage = (() => {
    try {
      const probe = '__bronte_wl__';
      window.localStorage.setItem(probe, '1');
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  })();

  const read = () => {
    if (!storage) return [];
    try {
      const parsed = JSON.parse(storage.getItem(KEY) || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((h) => typeof h === 'string' && h.length > 0);
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    if (!storage) return;
    try {
      storage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* quota or private mode: saving just does not persist */
    }
  };

  const emit = () => {
    const handles = read();
    document.dispatchEvent(
      new CustomEvent(EVENT, { detail: { handles: handles, count: handles.length } })
    );
  };

  const api = {
    available: () => Boolean(storage),
    list: () => read(),
    count: () => read().length,
    has: (handle) => read().indexOf(handle) !== -1,
    add(handle) {
      if (!handle) return false;
      const list = read();
      if (list.indexOf(handle) === -1) {
        list.push(handle);
        write(list);
        emit();
      }
      return true;
    },
    remove(handle) {
      if (!handle) return false;
      const list = read();
      const next = list.filter((h) => h !== handle);
      if (next.length !== list.length) {
        write(next);
        emit();
      }
      return false;
    },
    toggle(handle) {
      return api.has(handle) ? api.remove(handle) : api.add(handle);
    },
    /* Used when a saved product 404s. Silent: the shopper never asked to remove it. */
    prune(handles) {
      if (!handles || !handles.length) return;
      const drop = {};
      handles.forEach((h) => {
        drop[h] = true;
      });
      const next = read().filter((h) => !drop[h]);
      write(next);
      emit();
    },
  };

  window.BronteWishlist = api;

  const syncHearts = () => {
    const saved = read();
    document.querySelectorAll('[data-bronte-wishlist-toggle]').forEach((btn) => {
      const handle = btn.getAttribute('data-wishlist-handle');
      const on = handle ? saved.indexOf(handle) !== -1 : false;
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const name = btn.getAttribute('data-wishlist-title') || 'this product';
      btn.setAttribute('aria-label', (on ? 'Remove ' : 'Save ') + name + (on ? ' from your wishlist' : ' to your wishlist'));
    });
  };

  const syncCounts = () => {
    const count = read().length;
    document.querySelectorAll('[data-bronte-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-bronte-wishlist-link]').forEach((el) => {
      el.setAttribute('aria-label', count === 0 ? 'Wishlist' : 'Wishlist, ' + count + (count === 1 ? ' item' : ' items'));
      if (count === 0) {
        el.setAttribute('data-wishlist-empty', '');
      } else {
        el.removeAttribute('data-wishlist-empty');
      }
    });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-bronte-wishlist-toggle]');
    if (!btn) return;
    event.preventDefault();
    const handle = btn.getAttribute('data-wishlist-handle');
    if (!handle || !api.available()) return;
    api.toggle(handle);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === KEY) emit();
  });

  const cache = new Map();
  let renderSeq = 0;

  const initPage = (root) => {
    if (!root || root.dataset.wlReady) return;
    root.dataset.wlReady = 'true';

    const grid = root.querySelector('[data-wishlist-grid]');
    const tpl = root.querySelector('[data-wishlist-card-template]');
    if (!grid || !tpl) return;

    const currency = root.dataset.currency || 'GBP';
    const locale = root.dataset.locale || document.documentElement.lang || 'en-GB';
    const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const money = (cents) => {
      const amount = (Number(cents) || 0) / 100;
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(amount);
      } catch (e) {
        return amount.toFixed(2);
      }
    };

    const sized = (url, width) => {
      if (!url) return '';
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    };

    const fetchProduct = (handle) => {
      if (cache.has(handle)) return Promise.resolve(cache.get(handle));
      return fetch(rootUrl.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' },
      })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null)
        .then((data) => {
          const product = data && data.title ? data : null;
          cache.set(handle, product);
          return product;
        });
    };

    const buildCard = (product, handle) => {
      const node = tpl.content.firstElementChild.cloneNode(true);
      const url = product.url || rootUrl.replace(/\/$/, '') + '/products/' + handle;
      const image = product.featured_image || (product.images && product.images[0]) || '';

      node.querySelectorAll('[data-wl-link]').forEach((el) => {
        el.setAttribute('href', url);
      });

      const img = node.querySelector('[data-wl-img]');
      const frame = node.querySelector('[data-wl-frame]');
      if (img) {
        if (image) {
          img.src = sized(image, 600);
          img.srcset = sized(image, 300) + ' 300w, ' + sized(image, 600) + ' 600w, ' + sized(image, 900) + ' 900w';
          img.alt = product.title || '';
        } else {
          img.remove();
          if (frame) frame.setAttribute('data-empty', '');
        }
      }

      const title = node.querySelector('[data-wl-title]');
      if (title) title.textContent = product.title || '';

      const now = node.querySelector('[data-wl-price]');
      if (now) now.textContent = money(product.price);

      const was = node.querySelector('[data-wl-compare]');
      if (was) {
        if (product.compare_at_price && product.compare_at_price > product.price) {
          was.textContent = money(product.compare_at_price);
        } else {
          was.remove();
        }
      }

      const badge = node.querySelector('[data-wl-badge]');
      if (badge) {
        if (product.available === false) {
          badge.textContent = 'Sold out';
          badge.classList.add('bronte-product-card__badge--muted');
        } else if (product.compare_at_price && product.compare_at_price > product.price) {
          badge.textContent = 'Sale';
        } else {
          badge.remove();
        }
      }

      const heart = node.querySelector('[data-bronte-wishlist-toggle]');
      if (heart) {
        heart.setAttribute('data-wishlist-handle', handle);
        heart.setAttribute('data-wishlist-title', product.title || 'this product');
      }

      return node;
    };

    const setState = (state) => {
      root.setAttribute('data-state', state);
    };

    const render = () => {
      const seq = ++renderSeq;
      const handles = read();

      if (!api.available()) {
        grid.replaceChildren();
        setState('unavailable');
        return;
      }

      if (!handles.length) {
        grid.replaceChildren();
        setState('empty');
        return;
      }

      if (!grid.children.length) setState('loading');

      Promise.all(handles.map(fetchProduct)).then((items) => {
        if (seq !== renderSeq) return;

        const missing = [];
        const found = [];
        items.forEach((product, i) => {
          if (product) {
            found.push({ product: product, handle: handles[i] });
          } else {
            missing.push(handles[i]);
          }
        });

        if (missing.length) {
          api.prune(missing);
          return;
        }

        const frag = document.createDocumentFragment();
        found.forEach((entry) => frag.appendChild(buildCard(entry.product, entry.handle)));
        grid.replaceChildren(frag);
        setState(found.length ? 'ready' : 'empty');
        syncHearts();
      });
    };

    root.wlRender = render;
    render();
  };

  const initAll = () => {
    if (!api.available()) {
      document.documentElement.setAttribute('data-bronte-wishlist-unavailable', '');
    }
    document.querySelectorAll('[data-bronte-wishlist-page]').forEach(initPage);
    syncHearts();
    syncCounts();
  };

  document.addEventListener(EVENT, () => {
    syncHearts();
    syncCounts();
    document.querySelectorAll('[data-bronte-wishlist-page]').forEach((root) => {
      if (typeof root.wlRender === 'function') root.wlRender();
    });
  });

  /* Theme editor re-renders a section's markup, so re-init the fresh nodes. */
  document.addEventListener('shopify:section:load', () => {
    initAll();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
